import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Button, Spin, Tabs } from 'antd';
import { connect } from 'react-redux';
import CustomDrawer, { IdrawerRef } from '@/components/CustomDrawer';
import { formatAreaMobileStr } from '@/components/BaseMobileInput/helper';
import { getCardInfoByWyuid, updateCardInfo } from '@/redux/actions/guest';
import { BasicCardInfoItem, IguestInfo, MemberLevelItem } from '@/redux/reducers/guest';
import { fetchDesensitizedCardInfoByWyuid } from '@/api/guest';
import { PERM_CODES } from '@/config';
import useOrderLink from '@/hooks/useOrderLink';
import { renderIntlLabel } from '@/utils';

import BasicInfo from './BasicInfo';
import MemberInfo from './MemberInfo';
import CouponLeftTab from '../CouponLeftTab';
import RunningAccount from './RunningAccount';
import CheckinHistory from './CheckinHistory';
import HotelPoints from './HotelPoints';
import styles from './index.module.scss';

const { TabPane } = Tabs;

export interface IGuestDetailRef extends Omit<IdrawerRef, 'openDrawer'> {
  openDrawer: (record: { guestRecord: Partial<IguestInfo> }) => void;
}

interface GuestDetailDrawerProps {
  permissions?: Array<string>;
  basicCardInfo?: BasicCardInfoItem;
  memberLevelMap?: { [key: string]: MemberLevelItem };
  cardInfoLoading?: boolean;
  myRef?: any;
  onClose?: () => void;
  getCardInfoByWyuidFn?: (..._) => void;
  updateCardInfoFn?: (..._) => void;
}

const GuestDetailDrawer: React.FC<GuestDetailDrawerProps> = ({
  permissions,
  basicCardInfo,
  memberLevelMap,
  cardInfoLoading,
  myRef,
  onClose,
  getCardInfoByWyuidFn,
  updateCardInfoFn,
}) => {
  const [guestInfo, setGuestInfo] = useState<Partial<IguestInfo>>();
  const [showDesensitizeInfoLoading, setShowDesensitizeInfoLoading] = useState<boolean>(false);
  const [parentKey, setParentKey] = useState<string>('basicInfo');
  const [isFirstClick, setIsFirstClick] = useState<boolean>(true);
  const [desensitizedCardInfo, setDesensitizedCardInfo] = useState<any>({});
  const drawerRef = useRef<IdrawerRef>();
  const { linkToReservationDetail } = useOrderLink();

  const handleCancel = () => {
    drawerRef.current.closeDrawer();
    updateCardInfoFn({});
    setShowDesensitizeInfoLoading(false);
    setIsFirstClick(true);
    setDesensitizedCardInfo({});
  };

  const handleShowDesensitizeInfo = async () => {
    const { wyuid } = guestInfo;
    try {
      setShowDesensitizeInfoLoading(true);
      const res = await fetchDesensitizedCardInfoByWyuid({ wyuid });
      if (res?.code === 0) {
        const { name, certNo, mobile, email } = res.data || {};
        setIsFirstClick(false);
        setDesensitizedCardInfo(res?.data || {});
        updateCardInfoFn({
          basicCardInfo: {
            ...basicCardInfo,
            name,
            certNo,
            mobile,
            email,
          },
        });
      }
    } catch (error) {
      //
    } finally {
      setShowDesensitizeInfoLoading(false);
    }
  };

  const initBaseInfoCallBack = useCallback(
    (val) => getCardInfoByWyuidFn({ ...val, desensitizedCardInfo }),
    [desensitizedCardInfo],
  );

  const handleOpenDrawer = (record) => {
    drawerRef.current.openDrawer();
    const { guestRecord } = record;
    setGuestInfo(guestRecord);

    initBaseInfoCallBack({ wyuid: guestRecord?.wyuid });
  };

  const onTabChange = (key) => {
    setParentKey(key);

    if (['basicInfo', 'memberInfo'].includes(key)) {
      initBaseInfoCallBack({ wyuid: guestInfo?.wyuid });
    }
  };

  const onBasicEdit = () => {
    if (typeof onClose === 'function') {
      drawerRef.current.closeDrawer();
      handleCancel();
      onClose();
    }
  };

  const handleHotelPointsClick = (record) => {
    drawerRef.current.closeDrawer();
    linkToReservationDetail(record.resvNo);
  };

  const handleDetailClick = (record) => {
    drawerRef.current.closeDrawer();
    linkToReservationDetail(record.resvNo);
  };

  useEffect(() => {
    return () => {
      updateCardInfoFn({});
      setShowDesensitizeInfoLoading(false);
      setIsFirstClick(true);
      setDesensitizedCardInfo({});
    };
  }, []);

  useImperativeHandle(
    myRef,
    () => ({
      ...drawerRef.current,
      openDrawer: handleOpenDrawer,
    }),
    [],
  );

  return (
    <>
      <CustomDrawer
        ref={drawerRef}
        title={renderIntlLabel('order_action_guest_detail')}
        width="90%"
        footer={
          <>
            <Button type="ghost" onClick={handleCancel}>
              {renderIntlLabel('order_action_close')}
            </Button>
          </>
        }
        onClose={handleCancel}
      >
        <div className={styles.container}>
          <Spin spinning={showDesensitizeInfoLoading}>
            <div className={styles.info}>
              <div>
                <div className={styles.label}>{renderIntlLabel('order_roomorder_name')}</div>
                <div>{basicCardInfo?.name}</div>
              </div>
              <div>
                <div className={styles.label}>{renderIntlLabel('order_roomorder_cert_no')}</div>
                <div>{basicCardInfo?.certNo}</div>
              </div>
              <div>
                <div className={styles.label}>{renderIntlLabel('order_roomorder_mobile')}</div>
                <div>{formatAreaMobileStr(basicCardInfo?.mobile)}</div>
              </div>
              <div>
                <div className={styles.label}>
                  {renderIntlLabel('help_store_address_type_email')}
                </div>
                <div>{basicCardInfo?.email}</div>
              </div>
              {isFirstClick && permissions?.includes(PERM_CODES.GUEST_PLAINTEXT_INFO_QUERY) ? (
                <div style={{ marginLeft: 'auto', marginRight: '8px' }}>
                  <Button type="ghost" onClick={handleShowDesensitizeInfo}>
                    {renderIntlLabel('order_action_view')}
                  </Button>
                </div>
              ) : null}
            </div>
          </Spin>

          <Tabs type="card" activeKey={parentKey} onChange={onTabChange} destroyInactiveTabPane>
            <TabPane tab={renderIntlLabel('guest_tab_baseinfo')} key="basicInfo">
              <BasicInfo
                memberLevelMap={memberLevelMap}
                guestInfo={guestInfo}
                basicCardInfo={basicCardInfo}
                onBasicEdit={onBasicEdit}
                cardInfoLoading={cardInfoLoading}
              />
            </TabPane>
            <TabPane tab={renderIntlLabel('guest_tab_memberAccount')} key="memberInfo">
              <MemberInfo
                guestInfo={guestInfo}
                basicCardInfo={basicCardInfo}
                cardInfoLoading={cardInfoLoading}
              />
            </TabPane>
            {permissions.length > 0 && permissions.includes(PERM_CODES.GUEST_RUNNING_ACCOUNT) ? (
              <TabPane tab={renderIntlLabel('guest_card_label_balance')} key="runningAccount">
                <RunningAccount guestInfo={guestInfo} />
              </TabPane>
            ) : null}
            <TabPane tab={renderIntlLabel('channel_crs_label_coupon')} key="couponList">
              <CouponLeftTab guestInfo={guestInfo} basicCardInfo={basicCardInfo} />
            </TabPane>
            <TabPane tab={renderIntlLabel('guest_hotel_points')} key="hotelPoints">
              <HotelPoints guestInfo={guestInfo} onHotelPointsClick={handleHotelPointsClick} />
            </TabPane>
            {permissions?.includes(PERM_CODES.CHECKIN_HISTORY) ? (
              <TabPane tab={renderIntlLabel('guest_tab_checkinHistory')} key="checkinHistory">
                <CheckinHistory
                  guestInfo={guestInfo}
                  onClick={handleDetailClick}
                  onCloseDrawer={handleCancel}
                />
              </TabPane>
            ) : null}
          </Tabs>
        </div>
      </CustomDrawer>
    </>
  );
};

const mapStateToProps = (state) => {
  const { permissions } = state.user;
  return {
    permissions,
    ...state.guest,
  };
};

const ConnectComponent: React.FC<GuestDetailDrawerProps> = connect(mapStateToProps, {
  getCardInfoByWyuidFn: getCardInfoByWyuid,
  updateCardInfoFn: updateCardInfo,
})(React.memo(GuestDetailDrawer));

export default React.forwardRef<IGuestDetailRef, GuestDetailDrawerProps>((props, ref) => (
  <ConnectComponent {...props} myRef={ref} />
));
